'use client';

import { usePathname, useParams } from 'next/navigation';
import Link from 'next/link';
import JsonLd from '@/components/JsonLd';
import { SITE_URL } from '@/lib/siteConfig';

const capitalizeFirstLetter = (str) => {
    return str?.charAt(0).toUpperCase() + str?.slice(1);
};

// Mapeo de rutas en ambos idiomas
const translations = {
    'noticias': { es: 'Noticias', en: 'News' },
    'sobre-evanhub': { es: 'Sobre Evanhub', en: 'About' },
    'asesoria-empresarial': { es: 'Asesoria Empresarial', en: 'Business Consulting' },
    'equipo': { es: 'Equipo', en: 'Team' },
    'terminos-y-condiciones': { es: 'Terminos y Condiciones', en: 'Terms and Conditions' },
    'politicas-de-privacidad': { es: 'Politicas de Privacidad', en: 'Privacy Policies' },
    'contacto': { es: 'Contacto', en: 'Contact' },
    'politicas-de-cookies': { es: 'Politicas de Cookies', en: 'Cookie Policies' },
    'politicas-de-reembolso': { es: 'Politicas de Reembolso', en: 'Refund Policies' },
    'preguntas-frecuentes': { es: 'Preguntas Frecuentes', en: 'FAQ' },
    'areas/evaluacion-psiquiatrica': { es: 'Evaluación Psiquiátrica', en: 'Psychiatric Evaluation' },
    'areas/evaluacion-psicologica': { es: 'Evaluación Psicológica', en: 'Psychological Evaluation' },
    'areas/evaluacion-neurologica': { es: 'Evaluación Neurológica', en: 'Neurological Evaluation' },
    'areas/evaluacion-juridica': { es: 'Evaluación Jurídica', en: 'Legal Assessment' },
    'consentimiento-informado': { es: 'Consentimiento Informado', en: 'Informed Consent' },
};

// Nota: este componente es Client (usePathname/useParams) a propósito. Las páginas de contenido
// usan `export const dynamic = 'force-static'`, y bajo ese modo headers()/cookies() (APIs de
// Server Component) no reflejan la request real y rompen tanto este breadcrumb como el status
// 404 real de notFound(). usePathname()/useParams() sí resuelven bien en SSG/ISR.
const Breadcrumb = ({ customClass, customName }) => {
    const pathname = usePathname() || '';
    const params = useParams();
    const locale = params?.lang || 'es';

    const path = pathname.split('/').filter((x) => x);
    const separator = '/';

    const filteredSegments = path.filter(segment => segment !== 'en' && segment !== 'es');

    const breadcrumbLabels = filteredSegments.map((segment, index) => {
        let translatedSegment = translations[segment]?.[locale] || capitalizeFirstLetter(segment.replace(/-/g, ' '));
        if (index === filteredSegments.length - 1 && customName) {
            translatedSegment = customName;
        }
        return translatedSegment;
    });

    const breadcrumbJsonLd = path.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            {
                '@type': 'ListItem',
                position: 1,
                name: locale === 'es' ? 'Inicio' : 'Home',
                item: `${SITE_URL}/${locale}`,
            },
            ...breadcrumbLabels.map((name, index) => ({
                '@type': 'ListItem',
                position: index + 2,
                name,
                item: `${SITE_URL}/${locale}/${filteredSegments.slice(0, index + 1).join('/')}`,
            })),
        ],
    } : null;

    return (
        path.length > 0 && (
            <div className={`breadcrumbs ${customClass}`}>
                <JsonLd data={breadcrumbJsonLd} />
                <div className='container'>
                    <nav aria-label="Breadcrumb">
                        <ol>
                            <li key="home-list">
                                <Link href={`/${locale}`}>
                                    <span><i className="bi bi-house"></i></span>
                                </Link>
                            </li>

                            {filteredSegments.map((segment, index) => (
                                <li key={segment + `-list`}>
                                    <span className='separator'>{separator}</span>
                                    <span>{breadcrumbLabels[index]}</span>
                                </li>
                            ))}
                        </ol>
                    </nav>
                </div>
            </div>
        )
    );
};

export default Breadcrumb;
